<div class="sg-page">
  <header class="sg-header">
    <h1 class="sg-title">Warm Dusk — Style Guide</h1>
    <p class="sg-subtitle">
      Levende referentie van de nieuwe vormtaal: kleuren, knoppen, tekstvelden,
      dropdowns en keuzevelden. Gebruik deze pagina om designs uniform te houden.
    </p>
  </header>

  <!-- ============================ KLEUREN ============================ -->
  <section class="sg-section">
    <h2 class="sg-section__title">Kleur-tokens</h2>
    <p class="sg-section__info">
      Alle oppervlakken en accenten verwijzen naar CSS-variabelen. Amber
      (<code>--amber</code>) is het primaire accent / CTA, coral
      (<code>--cta</code>) het secundaire CTA-accent, en het warm-dusk blauw
      (<code>--primary</code>) de merkkleur / donkere shell.
    </p>
    <div class="sg-swatches">
      @for (token of colorTokens; track token) {
        <div class="sg-swatch">
          <span class="sg-swatch__chip" [style.background]="'var(' + token.variable + ')'"></span>
          <span class="sg-swatch__label">{{ token.label }}</span>
          <code class="sg-swatch__var">{{ token.variable }}</code>
          @if (token.hex) {
            <code class="sg-swatch__value">{{ token.hex }}</code>
          }
          @if (token.rgb) {
            <code class="sg-swatch__value">{{ token.rgb }}</code>
          }
        </div>
      }
    </div>
  </section>

  <!-- ============================ KNOPPEN ============================ -->
  <section class="sg-section">
    <h2 class="sg-section__title">Knoppen</h2>
    <p class="sg-section__info">
      Knoppen gebruiken de <code>eBtn</code>-directive: afgeronde hoeken
      (<code>--radius-lg</code>), <code>font-weight: 600</code> en
      kleur via de <code>color</code>-input. De primaire knop
      (<code>color="primary"</code>) is nu de amber CTA met donkerblauwe tekst —
      identiek aan de login-knop.
    </p>

    <h3 class="sg-subhead">Kleuren (size md)</h3>
    <div class="sg-row">
      @for (btn of buttonColors; track btn) {
        <button eBtn [color]="btn.color">{{ btn.label }}</button>
      }
    </div>

    <h3 class="sg-subhead">Primaire knop — alle groottes</h3>
    <div class="sg-row sg-row--baseline">
      @for (size of buttonSizes; track size) {
        <button eBtn color="primary" [size]="size">
          {{ size }}
        </button>
      }
    </div>

    <h3 class="sg-subhead">Met icoon</h3>
    <div class="sg-row">
      <button eBtn color="primary" icon="floppy-disk">Opslaan</button>
      <button eBtn color="cta" icon="trash">Verwijderen</button>
      <button eBtn color="secondary" icon="arrow-right-to-bracket">Verdergaan</button>
    </div>

    <h3 class="sg-subhead">Icoon-knoppen (squared &amp; rounded)</h3>
    <div class="sg-row">
      <button eBtn squared color="primary" icon="plus" size="sm"></button>
      <button eBtn squared color="primary" icon="pen" size="md"></button>
      <button eBtn squared color="secondary" icon="gear" size="lg"></button>
      <button eBtn rounded color="cta" icon="heart" size="md"></button>
      <button eBtn rounded color="success" icon="check" size="lg"></button>
    </div>

    <h3 class="sg-subhead">Hoek-varianten</h3>
    <div class="sg-row">
      <button eBtn color="primary">Afgerond (standaard)</button>
      <button eBtn color="primary" straightCorners>Rechte hoeken</button>
    </div>

    <h3 class="sg-subhead">Statussen</h3>
    <div class="sg-row sg-row--baseline">
      <button eBtn color="primary" [disabled]="true">Disabled</button>
      <button eBtn color="primary" [loading]="primaryLoading" (click)="triggerPrimaryLoading()">
        Klik voor loading
      </button>
    </div>

    <h3 class="sg-subhead">Volle breedte (block)</h3>
    <div class="sg-block-demo">
      <button eBtn block color="primary" icon="arrow-right-to-bracket">Aanmelden</button>
    </div>
  </section>

  <!-- ============================ TEKSTVELDEN ============================ -->
  <section class="sg-section">
    <h2 class="sg-section__title">Tekstvelden</h2>
    <p class="sg-section__info">
      Tekstvelden gebruiken <code>.form-control</code>: warme
      <code>--content-surface</code>-vulling, zachte border, hoogte 40&nbsp;px
      (sm = 32&nbsp;px), afgeronde hoeken en een amber focus-ring.
    </p>

    <div class="sg-grid">
      <div class="sg-field">
        <label class="sg-label">Standaard</label>
        <input class="form-control" [(ngModel)]="textValue" placeholder="Typ iets…" />
      </div>

      <div class="sg-field">
        <label class="sg-label">Ingevuld</label>
        <input class="form-control" [(ngModel)]="textValueFilled" />
      </div>

      <div class="sg-field">
        <label class="sg-label">Klein (sm)</label>
        <input class="form-control form-control-sm" placeholder="Compact veld…" />
      </div>

      <div class="sg-field">
        <label class="sg-label">Disabled</label>
        <input class="form-control" [(ngModel)]="disabledValue" [disabled]="true" />
      </div>

      <div class="sg-field">
        <label class="sg-label">Foutstaat (is-invalid)</label>
        <input class="form-control is-invalid" value="Ongeldige waarde" />
        <span class="sg-error">Dit veld is verplicht.</span>
      </div>

      <div class="sg-field">
        <label class="sg-label">Native select</label>
        <select class="form-control" [(ngModel)]="nativeSelectValue">
          <option value="">Selecteer…</option>
          @for (opt of selectOptions; track opt) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
        <span class="sg-field__note">
          Enkel de <em>gesloten</em> control volgt de stijl. Het uitklap-paneel
          wordt door het besturingssysteem getekend en kan niet cross-browser
          mee gestyled worden — gebruik <code>ng-select</code> (hieronder) waar
          het open paneel de vormtaal moet volgen.
        </span>
      </div>

      <div class="sg-field sg-field--wide">
        <label class="sg-label">Textarea</label>
        <textarea class="form-control" rows="3" [(ngModel)]="textareaValue" placeholder="Meerdere regels…"></textarea>
      </div>
    </div>
  </section>

  <!-- ============================ DROPDOWNS ============================ -->
  <section class="sg-section">
    <h2 class="sg-section__title">Dropdowns (ng-select)</h2>
    <p class="sg-section__info">
      De control deelt de tekstveld-stijl (afgerond + amber focus-ring). Het
      open paneel is losgekoppeld met een kleine gap (zodat de focus-ring niet
      botst), volledig afgerond en met een zachte schaduw — dezelfde stijl als de
      Human Design dropdowns.
    </p>

    <div class="sg-grid">
      <div class="sg-field">
        <label class="sg-label">Enkelvoudig</label>
        <ng-select
          [items]="selectOptions"
          bindLabel="label"
          bindValue="value"
          [formControl]="singleSelect"
          placeholder="Selecteer een app…"
        ></ng-select>
      </div>

      <div class="sg-field">
        <label class="sg-label">Meervoudig</label>
        <ng-select
          [items]="selectOptions"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [formControl]="multiSelect"
          placeholder="Selecteer meerdere…"
        ></ng-select>
      </div>

      <div class="sg-field">
        <label class="sg-label">Disabled</label>
        <ng-select
          [items]="selectOptions"
          bindLabel="label"
          bindValue="value"
          [formControl]="disabledSelect"
        ></ng-select>
      </div>
    </div>
  </section>

  <!-- ===================== MENU-DROPDOWN (e-dropdown-menu) ===================== -->
  <section class="sg-section">
    <h2 class="sg-section__title">Menu-dropdown (e-dropdown-menu)</h2>
    <p class="sg-section__info">
      Een derde variant: een custom activator-knop met een los menu-paneel
      (gebruikt o.a. in de Human Design controls-balk). In tegenstelling tot de
      <code>ng-select</code> hierboven is dit géén formulier-control maar een
      knop + menu met vrije inhoud. Het paneel volgt nu dezelfde vormtaal:
      losgekoppeld met een kleine gap, afgeronde hoeken en een zachte schaduw.
    </p>

    <div class="sg-grid">
      <div class="sg-field">
        <label class="sg-label">Enkelvoudige keuze</label>
        <e-dropdown-menu #sgMenu class="sg-menu" position="right" size="sm">
          <ng-template eSlot="activator">
            <button type="button" class="sg-menu-activator" (click)="sgMenu.toggleMenu()">
              <span class="sg-menu-value">{{ menuSelectedLabel }}</span>
              <i eIcon icon="chevron-down"></i>
            </button>
          </ng-template>

          <ng-template eSlot="menu">
            <div class="sg-menu-list">
              @for (opt of selectOptions; track opt) {
                <e-dropdown-menu-item
                  (click)="onMenuSelect(opt.label); sgMenu.toggleMenu()"
                  >
                  <span class="sg-menu-option" [class.sg-menu-option-active]="opt.label === menuSelectedLabel">
                    {{ opt.label }}
                  </span>
                </e-dropdown-menu-item>
              }
            </div>
          </ng-template>
        </e-dropdown-menu>
      </div>

      <div class="sg-field">
        <label class="sg-label">Met iconen</label>
        <e-dropdown-menu #sgMenuIcons class="sg-menu" position="right" size="sm">
          <ng-template eSlot="activator">
            <button type="button" class="sg-menu-activator" (click)="sgMenuIcons.toggleMenu()">
              <span class="sg-menu-value">Acties</span>
              <i eIcon icon="chevron-down"></i>
            </button>
          </ng-template>

          <ng-template eSlot="menu">
            <div class="sg-menu-list">
              <e-dropdown-menu-item icon="pen">
                <span class="sg-menu-option">Bewerken</span>
              </e-dropdown-menu-item>
              <e-dropdown-menu-item icon="copy">
                <span class="sg-menu-option">Dupliceren</span>
              </e-dropdown-menu-item>
              <e-dropdown-menu-item icon="trash" iconColor="danger">
                <span class="sg-menu-option">Verwijderen</span>
              </e-dropdown-menu-item>
            </div>
          </ng-template>
        </e-dropdown-menu>
      </div>
    </div>
  </section>

  <!-- ============================ KEUZEVELDEN ============================ -->
  <section class="sg-section">
    <h2 class="sg-section__title">Checkboxes &amp; radio's</h2>
    <p class="sg-section__info">
      Keuzevelden behouden hun eigen native gedrag (worden bewust uitgesloten van
      de tekstveld-overrides).
    </p>

    <div class="sg-row sg-row--baseline">
      <label class="sg-check">
        <input type="checkbox" [(ngModel)]="checkboxChecked" />
        <span>Aangevinkt</span>
      </label>
      <label class="sg-check">
        <input type="checkbox" [(ngModel)]="checkboxUnchecked" />
        <span>Niet aangevinkt</span>
      </label>
      <label class="sg-check">
        <input type="checkbox" [checked]="true" disabled />
        <span>Disabled</span>
      </label>
    </div>

    <div class="sg-row sg-row--baseline">
      <label class="sg-check">
        <input type="radio" name="sg-radio" value="a" [(ngModel)]="radioValue" />
        <span>Optie A</span>
      </label>
      <label class="sg-check">
        <input type="radio" name="sg-radio" value="b" [(ngModel)]="radioValue" />
        <span>Optie B</span>
      </label>
      <label class="sg-check">
        <input type="radio" name="sg-radio" value="c" [(ngModel)]="radioValue" />
        <span>Optie C</span>
      </label>
    </div>
  </section>

  <!-- ============================ LABELS ============================ -->
  <section class="sg-section">
    <h2 class="sg-section__title">Form-labels</h2>
    <p class="sg-section__info">
      Veld-labels: 11&nbsp;px, <code>font-weight: 500</code>,
      <code>letter-spacing: 1px</code>, uppercase, kleur <code>--secondary</code>.
    </p>
    <label class="sg-label">E-mailadres</label>
  </section>

  <!-- ============================ BANNERS &amp; ALERTS ============================ -->
  <section class="sg-section">
    <h2 class="sg-section__title">Banners &amp; alerts</h2>
    <p class="sg-section__info">
      Dark <code>p-announcement-banner</code> staat bovenaan de app (48&nbsp;px,
      fixed top). Light <code>e-alert</code> heeft twee varianten:
      <code>inline</code> (44&nbsp;px strip, default) en <code>block</code>
      (padded card). Beide ondersteunen <code>closable</code> en een
      <code>[actions]</code>-slot voor knoppen of links.
    </p>

    <h3 class="sg-subhead">Systeembanner (dark) — info / waarschuwing / foutmelding / succes</h3>
    <div class="sg-stack">
      <p-announcement-banner
        variant="info"
        icon="info-circle"
        title="Systeemonderhoud"
        message="Gepland op zondag 15 juni tussen 02:00 – 04:00 uur. Sla uw werk op voor deze tijd."
        >
        <a actions class="sg-banner-link" href="#" (click)="$event.preventDefault()">Meer info →</a>
      </p-announcement-banner>

      <p-announcement-banner
        variant="warning"
        icon="exclamation-triangle"
        title="Licentie"
        message="Uw licentie verloopt over 7 dagen. Verleng uw abonnement om onderbreking te voorkomen."
        >
        <a actions class="sg-banner-link" href="#" (click)="$event.preventDefault()">Abonnement verlengen →</a>
      </p-announcement-banner>

      <p-announcement-banner
        variant="danger"
        icon="times-circle"
        title="Serverstoring"
        message="Sommige functies zijn mogelijk niet beschikbaar."
        [closable]="false"
        >
        <a actions class="sg-banner-link" href="#" (click)="$event.preventDefault()">Status pagina →</a>
      </p-announcement-banner>

      <p-announcement-banner
        variant="success"
        icon="check-circle"
        title="Synchronisatie voltooid"
        message="Alle gegevens zijn succesvol bijgewerkt naar de laatste versie."
        >
        <a actions class="sg-banner-link" href="#" (click)="$event.preventDefault()">Bekijk wijzigingen →</a>
      </p-announcement-banner>
    </div>

    <h3 class="sg-subhead">Inline alert (light, default) — met actie en sluit-knop</h3>
    <div class="sg-stack">
      <e-alert color="info" withIcon closable>
        Systeem is bijgewerkt naar versie 2.4.1 — alle functies zijn actief en beschikbaar.
      </e-alert>
      <e-alert color="warning" withIcon closable>
        Let op — uw sessie verloopt over 10 minuten. Sla uw werk op om verlies te voorkomen.
        <a actions class="sg-alert-link" href="#" (click)="$event.preventDefault()">Verlengen</a>
      </e-alert>
      <e-alert color="danger" withIcon closable>
        Fout — verbinding mislukt. Controleer uw internetverbinding en probeer het opnieuw.
        <a actions class="sg-alert-link" href="#" (click)="$event.preventDefault()">Opnieuw →</a>
      </e-alert>
      <e-alert color="success" withIcon>
        Wijzigingen succesvol opgeslagen.
      </e-alert>
    </div>

    <h3 class="sg-subhead">Block alert (light) — met titel, body en actie-knoppen</h3>
    <div class="sg-grid sg-grid--alerts">
      <e-alert color="info" variant="block" withIcon closable header="Info">
        Dit is een informatieve melding. Gebruik dit voor algemene berichten of statusupdates die de gebruiker op de hoogte stellen.
      </e-alert>
      <e-alert color="warning" variant="block" withIcon closable header="Waarschuwing">
        Let op! Deze actie kan gevolgen hebben. Controleer de gegevens voordat u verdergaat met de volgende stap.
      </e-alert>
      <e-alert color="danger" variant="block" withIcon closable header="Verbinding met de server verbroken">
        Er is een fout opgetreden. De verbinding met de server is verbroken. Probeer het opnieuw of neem contact op met support.
        <div actions class="sg-row">
          <button eBtn color="danger" size="sm">Opnieuw proberen</button>
          <button eBtn color="secondary" size="sm">Contact support →</button>
        </div>
      </e-alert>
      <e-alert color="success" variant="block" withIcon closable header="Wijzigingen opgeslagen">
        Uw aanpassingen zijn succesvol bewaard. U kunt veilig verdergaan met de volgende stap.
      </e-alert>
    </div>
  </section>
</div>
